import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Page,
  Button,
  Spinner,
  Badge,
  InputField,
  TextareaField,
} from "@ui";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  useAgent,
  useUpdateAgent,
  type AgentInput,
  type AgentDocument,
} from "@agentic_system/api/agent";
import { useTools } from "@agentic_system/api/tool";
import { formatFileSize, type Document } from "@agentic_system/api/document";
import { DocumentSelectorDrawer } from "./document-selector-drawer";
import { StructuredRenderer } from "@ui/components/spec-renderer";
import {
  ArrowLeft,
  Check,
  Plus,
  X,
  Sparkles,
  Bot,
  FileText,
  Image,
  Table,
  File,
  QrCode,
} from "lucide-react";

// --- Constants ---

const SYSTEM_PROMPT_TYPE_LABELS: Record<string, string> = {
  connected_user: "Utilisateur connecte",
  profile_entrypoint: "Point d'entree profil",
};

const PROFILE_CLASS_OPTIONS = [
  { value: "", label: "Tous les profils" },
  { value: "Profile::Candidat", label: "Candidat" },
  { value: "Profile::Enseigne", label: "Enseigne" },
  { value: "Profile::Immo", label: "Bailleur" },
  { value: "Profile::Partner", label: "Partenaire" },
  { value: "Profile::ShoppingCenter", label: "Centre commercial" },
  { value: "Profile::Union", label: "Union" },
  { value: "Profile::Ville", label: "Ville" },
];

const ENTRYPOINT_EXTRA_OPTIONS = [
  { value: "Location", label: "Emplacement" },
  { value: "PointOfSale", label: "Point de vente" },
];

const ICON_OPTIONS = [
  { value: "search", label: "Recherche" },
  { value: "lightbulb", label: "Idee" },
  { value: "briefcase", label: "Business" },
  { value: "file-text", label: "Document" },
  { value: "settings", label: "Parametres" },
  { value: "help-circle", label: "Aide" },
  { value: "message-circle", label: "Message" },
  { value: "user", label: "Utilisateur" },
  { value: "map-pin", label: "Localisation" },
  { value: "building-2", label: "Entreprise" },
  { value: "trending-up", label: "Tendance" },
  { value: "star", label: "Etoile" },
  { value: "sparkles", label: "Magie" },
  { value: "rocket", label: "Lancement" },
  { value: "target", label: "Objectif" },
  { value: "zap", label: "Eclair" },
  { value: "bot", label: "Robot" },
];

// --- Helpers ---

interface DisplayDocument {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  thumbnail_url: string | null;
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith("image/")) return Image;
  if (contentType === "application/pdf") return FileText;
  if (contentType.includes("wordprocessingml")) return FileText;
  if (contentType.includes("spreadsheetml")) return Table;
  return File;
}

function Card({ title, children, actions, flush = false }: {
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  flush?: boolean;
}) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-lg">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {actions}
      </div>
      <div className={flush ? "" : "px-5 py-4"}>{children}</div>
    </div>
  );
}

// --- Form values ---

interface FormValues {
  label: string;
  user_facing_question: string;
  system_prompt: string;
  profile_class: string;
  icon: string;
  published: boolean;
  qr_code: boolean;
  marketing_title: string;
  marketing_content: string;
  tool_ids: number[];
}

function SaveButton() {
  const { isSubmitting } = useFormikContext();
  return (
    <Button type="submit" form="agent-detail-form" loading={isSubmitting}>
      Enregistrer
    </Button>
  );
}

// --- Main component ---

export function AgentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agentId = id ? Number(id) : 0;
  const { data: agent, isLoading: agentLoading } = useAgent(agentId);
  const { data: tools, isLoading: toolsLoading } = useTools();
  const updateAgent = useUpdateAgent();

  const [documentSelectorOpen, setDocumentSelectorOpen] = useState(false);
  const [illustrationFile, setIllustrationFile] = useState<File | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<DisplayDocument[]>([]);

  useEffect(() => {
    if (agent?.documents) setSelectedDocuments(agent.documents);
  }, [agent?.documents]);

  const isLoading = agentLoading || toolsLoading;
  const enabledTools = tools?.filter((t) => t.enabled) ?? [];

  if (isLoading) {
    return (
      <Page title="Agent">
        <div className="flex items-center justify-center py-24"><Spinner /></div>
      </Page>
    );
  }

  if (!agent) {
    return (
      <Page title="Agent">
        <div className="text-center py-24 text-slate-400">Agent introuvable.</div>
      </Page>
    );
  }

  const spt = agent.system_prompt_type;
  const isEntrypoint = spt === "profile_entrypoint";
  const isConnectedUser = spt === "connected_user";
  const hasSchema = true;

  const backPath = `/system/agentic/agents/${
    isEntrypoint ? "profile-entrypoint" : "connected-user"
  }`;

  const initialValues: FormValues = {
    label: agent.label || "",
    user_facing_question: agent.user_facing_question || "",
    system_prompt: agent.system_prompt || "",
    profile_class: agent.profile_class || "",
    icon: agent.icon || "",
    published: !!agent.published_at,
    qr_code: !!agent.qr_code,
    marketing_title: agent.marketing_title || "",
    marketing_content: agent.marketing_content || "",
    tool_ids: agent.tool_ids || [],
  };

  const handleSubmit = async (values: FormValues) => {
    const payload: AgentInput & { illustration?: globalThis.File | null } = {
      label: values.label,
      user_facing_question: values.user_facing_question || undefined,
      system_prompt: values.system_prompt || undefined,
      profile_class: values.profile_class || null,
      icon: values.icon || null,
      published_at: values.published ? new Date().toISOString() : null,
      qr_code: values.qr_code,
      marketing_title: values.marketing_title || null,
      marketing_content: values.marketing_content || null,
      tool_ids: values.tool_ids,
      document_ids: selectedDocuments.map((d) => d.id),
    };
    if (illustrationFile) {
      payload.illustration = illustrationFile;
    }
    await updateAgent.mutateAsync({ id: agentId, input: payload });
  };

  const handleAddDocuments = (docs: Document[]) => {
    setSelectedDocuments((prev) => {
      const existingIds = new Set(prev.map((d) => d.id));
      const newDocs = docs.filter((d) => !existingIds.has(d.id));
      return [...prev, ...newDocs];
    });
  };

  const profileClassOptions = isEntrypoint
    ? [...PROFILE_CLASS_OPTIONS, ...ENTRYPOINT_EXTRA_OPTIONS]
    : PROFILE_CLASS_OPTIONS;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ values, setFieldValue }) => (
        <Page
          data-testid="agent-detail"
          title={
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(backPath)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Bot className="w-5 h-5 text-slate-400" />
              <span>{agent.label}</span>
              <Badge
                label={SYSTEM_PROMPT_TYPE_LABELS[spt] ?? spt}
                color="purple"
                size="sm"
              />
              <Badge
                label={agent.published_at ? "Publie" : "Brouillon"}
                color={agent.published_at ? "green" : "default"}
                size="sm"
              />
            </div>
          }
          actions={<SaveButton />}
        >
          <Form id="agent-detail-form">
            <div className="grid grid-cols-3 gap-6">
              {/* -------- Left column -------- */}
              <div className="col-span-1 space-y-6">

                {/* General */}
                <Card title="General">
                  <div className="space-y-4">
                    <Field name="label">
                      {({ field }: { field: any }) => (
                        <InputField {...field} label="Label" placeholder="Nom de l'agent" />
                      )}
                    </Field>

                    <Field name="user_facing_question">
                      {({ field }: { field: any }) => (
                        <TextareaField
                          {...field}
                          label="Question utilisateur"
                          rows={2}
                          placeholder="Comment puis-je vous aider ?"
                        />
                      )}
                    </Field>

                    <Field name="profile_class">
                      {({ field }: { field: any }) => (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Type de profil cible
                          </label>
                          <select
                            {...field}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
                          >
                            {profileClassOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </Field>

                    <Field name="icon">
                      {({ field }: { field: any }) => (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Icone</label>
                          <select
                            {...field}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
                          >
                            <option value="">Aucune icone</option>
                            {ICON_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </Field>
                  </div>
                </Card>

                {/* Status */}
                <Card title="Publication">
                  <div className="space-y-3">
                    <Field name="published">
                      {({ field }: { field: any }) => (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            className="w-4 h-4 rounded border-slate-300 text-default-600 focus:ring-2 focus:ring-default-500 focus:ring-offset-0"
                          />
                          <div>
                            <span className="text-sm font-medium text-slate-700">Publie</span>
                            <p className="text-xs text-slate-500">Visible pour les utilisateurs.</p>
                          </div>
                        </label>
                      )}
                    </Field>

                    {isEntrypoint && (
                      <Field name="qr_code">
                        {({ field }: { field: any }) => (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              name={field.name}
                              className="w-4 h-4 rounded border-slate-300 text-default-600 focus:ring-2 focus:ring-default-500 focus:ring-offset-0"
                            />
                            <div>
                              <span className="text-sm font-medium text-slate-700">QR Code</span>
                              <p className="text-xs text-slate-500">Un seul par type de profil.</p>
                            </div>
                          </label>
                        )}
                      </Field>
                    )}
                  </div>
                </Card>

              </div>

              {/* -------- Right column -------- */}
              <div className="col-span-2 space-y-6">

                {/* System Prompt */}
                <Card title="Prompt systeme">
                  <Field name="system_prompt">
                    {({ field }: { field: any }) => (
                      <TextareaField
                        {...field}
                        rows={10}
                        className="font-mono text-sm"
                        placeholder="Instructions pour le comportement de l'agent..."
                      />
                    )}
                  </Field>
                </Card>

                {/* Schema — for profile_entrypoint and schema */}
                {hasSchema && (
                  <Card
                    title="Sortie structuree"
                    actions={
                      <Button
                        type="button"
                        variant="white"
                        size="sm"
                        icon={Sparkles}
                        onClick={() => navigate(`/system/agentic/agents/${agentId}/schema`)}
                      >
                        Schema Builder
                      </Button>
                    }
                  >
                    {agent.active_schema ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Badge label={`v${agent.active_schema.version}`} color="blue" size="sm" />
                          <span className="text-xs text-slate-500">
                            {new Date(agent.active_schema.created_at).toLocaleDateString("fr-FR", {
                              day: "numeric", month: "short", year: "numeric",
                              hour: "2-digit", minute: "2-digit",
                            })}
                          </span>
                        </div>
                        {agent.active_schema.prompt && (
                          <p className="text-sm text-slate-600 italic">{agent.active_schema.prompt}</p>
                        )}
                        {agent.active_schema.render_spec && agent.active_schema.example ? (
                          <StructuredRenderer
                            spec={agent.active_schema.render_spec as any}
                            data={agent.active_schema.example}
                          />
                        ) : (
                          <pre className="text-xs font-mono bg-slate-50 border border-slate-200 rounded p-3 max-h-72 overflow-auto">
                            {JSON.stringify(agent.active_schema.schema, null, 2)}
                          </pre>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Sparkles className="w-7 h-7 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">
                          Aucun schema actif. Utilisez le Schema Builder pour en creer un.
                        </p>
                      </div>
                    )}
                  </Card>
                )}

                {/* Marketing — only for connected_user */}
                {isConnectedUser && (
                  <Card title="Marketing">
                    <div className="space-y-4">
                      <Field name="marketing_title">
                        {({ field }: { field: any }) => (
                          <InputField
                            {...field}
                            label="Titre marketing"
                            placeholder="ex: Trouvez votre franchise ideale"
                          />
                        )}
                      </Field>

                      <Field name="marketing_content">
                        {({ field }: { field: any }) => (
                          <TextareaField
                            {...field}
                            label="Contenu marketing"
                            rows={4}
                            placeholder="Description marketing de l'agent..."
                          />
                        )}
                      </Field>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Illustration
                        </label>
                        {agent.illustration_url && !illustrationFile && (
                          <div className="mb-2">
                            <img
                              src={agent.illustration_url}
                              alt="Illustration"
                              className="w-32 h-32 object-cover rounded-lg border border-slate-200"
                            />
                          </div>
                        )}
                        {illustrationFile && (
                          <div className="mb-2">
                            <img
                              src={URL.createObjectURL(illustrationFile)}
                              alt="Nouvelle illustration"
                              className="w-32 h-32 object-cover rounded-lg border border-slate-200"
                            />
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setIllustrationFile(e.target.files?.[0] ?? null)}
                          className="text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-300 file:text-sm file:font-medium file:bg-white file:text-slate-700 hover:file:bg-slate-50"
                        />
                      </div>
                    </div>
                  </Card>
                )}

                {/* Documents */}
                <Card
                  title="Documents"
                  actions={
                    <Button
                      type="button"
                      variant="white"
                      size="sm"
                      icon={Plus}
                      onClick={() => setDocumentSelectorOpen(true)}
                    >
                      Ajouter
                    </Button>
                  }
                >
                  {selectedDocuments.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {selectedDocuments.map((doc) => {
                        const Icon = getFileIcon(doc.content_type);
                        return (
                          <div key={doc.id} className="flex items-center gap-3 px-5 py-3">
                            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {doc.thumbnail_url ? (
                                <img src={doc.thumbnail_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Icon className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-slate-800 truncate">{doc.filename}</div>
                              <div className="text-xs text-slate-500">{formatFileSize(doc.byte_size)}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedDocuments((prev) => prev.filter((d) => d.id !== doc.id))}
                              className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="w-7 h-7 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">
                        Aucun document selectionne.
                      </p>
                    </div>
                  )}
                </Card>

                {/* Tools */}
                <Card title="Outils" flush>
                  {enabledTools.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {enabledTools.map((tool) => {
                        const isEntrypointGet = isEntrypoint && tool.label === "get_current_profile";
                        const isChecked = values.tool_ids.includes(tool.id);
                        return (
                          <label
                            key={tool.id}
                            className={`flex items-start gap-3 px-5 py-2.5 ${
                              isEntrypointGet ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50 cursor-pointer"
                            }`}
                          >
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={isChecked}
                              disabled={isEntrypointGet}
                              onClick={() => {
                                if (isEntrypointGet) return;
                                const next = isChecked
                                  ? values.tool_ids.filter((tid) => tid !== tool.id)
                                  : [...values.tool_ids, tool.id];
                                setFieldValue("tool_ids", next);
                              }}
                              className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                                isChecked && !isEntrypointGet
                                  ? "bg-default-600 border-default-600 text-white"
                                  : "border-slate-300 bg-white"
                              }`}
                            >
                              {isChecked && !isEntrypointGet && <Check className="w-3 h-3" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-xs text-slate-800">
                                {tool.label}
                                {isEntrypointGet && (
                                  <span className="ml-1 text-slate-400 font-normal">(auto)</span>
                                )}
                              </div>
                              {tool.description && (
                                <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic text-center py-6">Aucun outil disponible.</p>
                  )}
                </Card>
              </div>

            </div>
          </Form>

          <DocumentSelectorDrawer
            open={documentSelectorOpen}
            onClose={() => setDocumentSelectorOpen(false)}
            onAdd={handleAddDocuments}
          />

        </Page>
      )}
    </Formik>
  );
}
